import {Component, EffectRef, EventEmitter, Input, InputSignal, OnDestroy, Output, effect, inject, input, untracked} from '@angular/core';
import {DialogModule} from "@syncfusion/ej2-angular-popups";
import {Subscription} from "rxjs";
import {DialogService} from "../../../shared/services/dialog.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {LanguageService} from "../../../shared/services/language.service";
import {DialogDocumentsUploadComponent} from "../../documents/dialog-documents-upload/dialog-documents-upload.component";
import {ChatStore} from '../chat-store/chat-store';
import {ChatUpdateRequest} from '../../../shared/types/generated/chatUpdateRequest';
import {ProfileService} from '../../../shared/services/profile.service';
import {ToasterService} from '../../../shared/services/toaster.service';
import {ChatResource} from '../../../shared/types/generated/chatResource';
import {FileStorageResource} from '../../../shared/types/generated/fileStorageResource';
import {StepItem} from '../../../shared/types/help-types/stepsInterface';
import {UserMinimalResource} from '../../../shared/types/generated/userMinimalResource';
import {GroupMinimalResource} from '../../../shared/types/generated/groupMinimalResource';

@Component({
    selector: 'app-dialog-chat-create-group',
    imports: [
        DialogModule,
        StepHeaderComponent,
        HeadlineComponent,
        StepContentComponent,
        FormButtonComponent,
        TooltipComponent,
        FormLabelComponent,
        FormInputComponent,
        FormUserGroupMailComponent,
        TranslatePipe,
        DialogDocumentsUploadComponent
    ],
    templateUrl: './dialog-chat-create-group.component.html',
    styleUrl: './dialog-chat-create-group.component.scss'
})
export class DialogChatCreateGroupComponent implements OnDestroy {
  public dialogService: DialogService = inject(DialogService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private toaster: ToasterService = inject(ToasterService);
  private languageService: LanguageService = inject(LanguageService);
  private profileService: ProfileService = inject(ProfileService);
  private chatStore: InstanceType<typeof ChatStore> = inject(ChatStore);

  public editingChat: InputSignal<ChatResource | null> = input<ChatResource | null>(null);
  @Input() public visible: boolean = false;
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public chatSaved: EventEmitter<void> = new EventEmitter<void>();

  public activeStep: number = 0;
  public steps: StepItem[] = [
    {id: 'content', title: this.languageService.getTranslationByKey('CHATCONTENTMENUTEXT'), help: this.languageService.getTranslationByKey('CHATCONTENTMENUHELP')},
    {id: 'groupimage', title: this.languageService.getTranslationByKey('CHATGROUPIMAGEMENUTEXT'), help: this.languageService.getTranslationByKey('CHATGROUPIMAGEMENUHELP'), optional: true} ,
    {id: 'participants',title: this.languageService.getTranslationByKey('CHATGROUPPARTICIPANTSMENUTEXT'), help: this.languageService.getTranslationByKey('CHATGROUPPARTICIPANTSMENUHELP')}
  ];
  public participantsUserIDs: number[] = [];
  public participantsGroupIDs: number[] = [];
  public participantsAdminUserIDs: number[] =[];
  public participantsUserModels: UserMinimalResource[] = [];
  public participantsGroupModels: GroupMinimalResource[] = [];
  public chatForm: FormGroup = this.formBuilder.group({
    title: ['', [Validators.maxLength(60)]]
  });
  public mediaFiles: FileStorageResource[] = [];
  public isUserGroupSelectionError: boolean = false;
  private subscriptions: Subscription[] = [];

  private chatToEditEffect: EffectRef = effect(() => {
    if(this.editingChat() !== null) {
      untracked(() => {
          this.chatForm.patchValue(
            {
              title: this.editingChat()!.custom_name ?? ''
            }
          );
          if(this.editingChat()) {
            this.mediaFiles = this.editingChat()?.custom_avatar === null ? [] : [this.editingChat()!.custom_avatar!];
          }
          this.participantsUserModels = this.editingChat()?.users ?? [];
          this.participantsUserIDs = this.editingChat()?.users.map(user => user.id) ?? [];
          this.participantsGroupIDs = this.editingChat()?.groups.map(group => Number(group)) ?? [];
          this.participantsAdminUserIDs = this.editingChat()?.admin_users.map(user => user.id) ?? [];
      })
    }
  });

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public get stepTitles(): string[] {
    return this.steps?.map(step => step.title) ?? [];
  }

  public get hasSelectedParticipants(): boolean {
    return this.participantsUserIDs.length > 0 || this.participantsGroupIDs.length > 0;
  }

  public handleStepChange(step: number | null = null): void {
    this.isUserGroupSelectionError = false;

    const targetStep = step ?? this.activeStep + 1;

    if (targetStep < 0 || targetStep >= this.steps.length) {
      return;
    }

    if (targetStep < this.activeStep) {
      this.activeStep = targetStep;
      return;
    }

    while (this.activeStep < targetStep) {
      const currentStep = this.steps[this.activeStep];

      if (!currentStep) {
        break;
      }

      if (
        currentStep.id === 'participants' &&
        !this.hasSelectedParticipants
      ) {
        this.isUserGroupSelectionError = true;
        break;
      }

      if (
        currentStep.id === 'content' &&
        this.chatForm.controls['title'].invalid
      ) {
        this.chatForm.controls['title'].markAsDirty();
        break;
      }

      this.activeStep++;
    }
  }

  public save(): void {
    const selfId = this.profileService.profile()?.user.id;
    const hasParticipantSelectionError = !this.hasSelectedParticipants;
    this.isUserGroupSelectionError = hasParticipantSelectionError;

    if (hasParticipantSelectionError) {
      this.activeStep = this.steps.findIndex((step: StepItem) => step.id === 'participants');
    }

    if (this.chatForm.invalid || hasParticipantSelectionError || !selfId) {
      this.toaster.error(
        this.languageService.getTranslationByKey('CREATEGROUPCHATVALIDATIONERRORTITLE'),
        this.languageService.getTranslationByKey('CREATEGROUPCHATVALIDATIONERRORMESSAGE')
      );
    } else {
      const mappedData: ChatUpdateRequest = {
        custom_name: this.chatForm.controls['title'].value,
        custom_avatar: {
          file_storage_uuid: this.mediaFiles[0]?.uuid ?? null
        },
        user_ids: this.participantsUserIDs.includes(selfId) ? this.participantsUserIDs : [...this.participantsUserIDs, selfId],
        admin_user_ids: this.participantsAdminUserIDs.includes(selfId)? this.participantsAdminUserIDs : [...this.participantsAdminUserIDs, selfId],
        group_ids: this.participantsGroupIDs
      }
      // enforce users are admin users if only two users are part of that chat and no groups
      if(mappedData.user_ids.length === 2 && mappedData.group_ids.length === 0) {
        mappedData.admin_user_ids = mappedData.user_ids;
      }

      if (!this.editingChat()) {
        this.chatStore.createChat(mappedData, () => {
          this.chatSaved.emit();
          this.close();
        });
      } else {
        this.chatStore.updateChat(this.editingChat()!.id, mappedData);
        this.close();
      }
    }
  }

  public close(): void {
    this.chatForm.reset();
    this.participantsUserIDs = [];
    this.participantsGroupIDs = [];
    this.participantsAdminUserIDs = [];
    this.participantsUserModels = [];
    this.participantsGroupModels = [];
    this.mediaFiles = [];
    this.activeStep = 0;
    this.visibleChange.emit(false);
  }
}
